// src/components/planner/components/MonthCalendar.tsx
// ✅ Desktop: click no dia abre modal | Mobile: só botões abrem modal

import React, { useState, useEffect } from 'react';
import MonthEventModal from '../modals/MonthEventModal';
import DayEventsModal from '../modals/DayEventsModal';
import { Plus, Pencil } from 'lucide-react';
import EventDetailPopover from './EventDetailPopover';
import EditMonthEventModal from '../modals/EditMonthEventModal';

const API_BASE_URL = '/api';

interface MonthCalendarProps {
    currentMonth: Date;
    events: any[];
    onReload: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const MonthCalendar: React.FC<MonthCalendarProps> = ({
    currentMonth,
    onReload,
    onShowToast
}) => {
    const [monthEvents, setMonthEvents] = useState<any[]>([]);
    const [showEventModal, setShowEventModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [showDayEventsModal, setShowDayEventsModal] = useState(false);
    const [selectedDayEvents, setSelectedDayEvents] = useState<any[]>([]);
    const [activeMobileDay, setActiveMobileDay] = useState<number | null>(null);
    const activeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
    const [popoverEvent, setPopoverEvent] = useState<any | null>(null);
    const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
    const [editingEvent, setEditingEvent] = useState<any | null>(null);
    const blockDayClickRef = React.useRef(false);

    // ✅ Detecta se é dispositivo touch/mobile
    const isTouchDevice = () =>
        typeof window !== 'undefined' &&
        (window.matchMedia('(hover: none)').matches || window.innerWidth <= 768);

    useEffect(() => {
        loadMonthEvents();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMonth]);

    const loadMonthEvents = async () => {
        try {
            const token = localStorage.getItem('accessToken');
            const month = currentMonth.getMonth() + 1;
            const year = currentMonth.getFullYear();

            const response = await fetch(
                `${API_BASE_URL}/planner/month-events?month=${month}&year=${year}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.ok) {
                const data = await response.json();
                setMonthEvents(data.events || []);
            }
        } catch (error) {
            console.error('Erro ao carregar eventos mensais:', error);
        }
    };

    const handleDayClick = (date: Date, day: number) => {
        console.log('🔴 handleDayClick disparou, blockDayClickRef:', blockDayClickRef.current);
        if (blockDayClickRef.current) return;
        if (isTouchDevice()) {
            // Se já está ativo, não faz nada (deixa os botões ali)
            if (activeMobileDay === day) return;

            // Limpa timer anterior
            if (activeTimerRef.current) clearTimeout(activeTimerRef.current);

            // Ativa os botões neste dia
            setActiveMobileDay(day);

            // Após 5s, esconde
            activeTimerRef.current = setTimeout(() => {
                setActiveMobileDay(null);
            }, 5000);
            return;
        }
        // Desktop: abre modal normalmente
        setSelectedDate(date);
        setShowEventModal(true);
    };

    const handleAddClick = (e: React.MouseEvent, date: Date) => {
        e.stopPropagation();
        setSelectedDate(date);
        setShowEventModal(true);
    };

    const handleEditClick = (e: React.MouseEvent, date: Date, events: any[]) => {
        e.stopPropagation();
        setSelectedDate(date);
        setSelectedDayEvents(events);
        setShowDayEventsModal(true);
    };

    // ── 3. HANDLER — adicionar como nova função no componente ───
    const handleEventClick = (e: React.MouseEvent, event: any) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        blockDayClickRef.current = true;
        setTimeout(() => { blockDayClickRef.current = false; }, 0);
        console.log('🟢 handleEventClick disparou:', event.title);
        console.log('🟢 popoverAnchor:', e.currentTarget);
        setPopoverEvent(event);
        setPopoverAnchor(e.currentTarget as HTMLElement);
    };

    const getEventsForDay = (day: number) => {
        const dateStr = new Date(
            currentMonth.getFullYear(),
            currentMonth.getMonth(),
            day
        ).toISOString().split('T')[0];

        return monthEvents.filter(event => {
            const eventDate = new Date(event.date).toISOString().split('T')[0];
            return eventDate === dateStr;
        });
    };

    const isToday = (day: number) => {
        const today = new Date();
        return (
            day === today.getDate() &&
            currentMonth.getMonth() === today.getMonth() &&
            currentMonth.getFullYear() === today.getFullYear()
        );
    };

    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
        days.push({ day: null, events: [] });
    }

    for (let day = 1; day <= daysInMonth; day++) {
        days.push({ day, events: getEventsForDay(day) });
    }

    return (
        <>
            <div className="plnr-mc-calendar">
                {/* DIAS DA SEMANA */}
                <div className="plnr-mc-weekdays">
                    {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(day => (
                        <div key={day} className="plnr-mc-weekday">{day}</div>
                    ))}
                </div>

                {/* GRID DE DIAS */}
                <div className="plnr-mc-days">
                    {days.map((dayData, index) => {
                        if (!dayData.day) {
                            return <div key={`empty-${index}`} className="plnr-mc-day empty" />;
                        }

                        const dayDate = new Date(
                            currentMonth.getFullYear(),
                            currentMonth.getMonth(),
                            dayData.day
                        );

                        return (
                            <div
                                key={dayData.day}
                                className={`plnr-mc-day ${isToday(dayData.day) ? 'today' : ''} ${activeMobileDay === dayData.day ? 'mobile-active' : ''}`}
                                onClick={() => handleDayClick(dayDate, dayData.day)}
                            >
                                <div className="plnr-mc-day-number">{dayData.day}</div>

                                {/* EVENTOS DO DIA */}
                                {dayData.events.length > 0 && (
                                    <div className="plnr-mc-day-events">
                                        {dayData.events.slice(0, 2).map((event: any, idx: number) => (
                                            <div
                                                key={event._id || idx}
                                                className="plnr-mc-event-mini"
                                                style={{
                                                    borderLeft: `3px solid ${event.color || 'var(--platform-accent-light)'}`,
                                                    cursor: 'pointer',
                                                }}
                                                title={event.title}
                                                onClick={(e) => handleEventClick(e, event)}
                                            >
                                                {event.title}
                                            </div>
                                        ))}
                                        {dayData.events.length > 2 && (
                                            <span className="plnr-mc-more">
                                                +{dayData.events.length - 2} mais
                                            </span>
                                        )}
                                    </div>
                                )}

                                {/* ✅ Botões: lápis só aparece se tiver evento, + sempre */}
                                <div className="plnr-mc-day-actions">
                                    {dayData.events.length > 0 && (
                                        <button
                                            className="plnr-mc-action-btn"
                                            onClick={(e) => handleEditClick(e, dayDate, dayData.events)}
                                            title="Ver/editar eventos"
                                        >
                                            <Pencil size={10} />
                                        </button>
                                    )}
                                    <button
                                        className="plnr-mc-action-btn"
                                        onClick={(e) => handleAddClick(e, dayDate)}
                                        title="Adicionar evento"
                                    >
                                        <Plus size={12} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {showEventModal && selectedDate && (
                <MonthEventModal
                    date={selectedDate}
                    onClose={() => { setShowEventModal(false); setSelectedDate(null); }}
                    onSave={() => {
                        setShowEventModal(false);
                        setSelectedDate(null);
                        loadMonthEvents();
                        onReload();
                    }}
                    onShowToast={onShowToast}
                />
            )}

            {showDayEventsModal && selectedDate && (
                <DayEventsModal
                    date={selectedDate}
                    events={selectedDayEvents}
                    onClose={() => setShowDayEventsModal(false)}
                    onReload={() => { setShowDayEventsModal(false); loadMonthEvents(); onReload(); }}
                    onShowToast={onShowToast}
                />
            )}


            {popoverEvent && popoverAnchor && (
                <EventDetailPopover
                    event={popoverEvent}
                    anchorEl={popoverAnchor}
                    onClose={() => {
                        setPopoverEvent(null);
                        setPopoverAnchor(null);
                    }}
                    onEdit={() => {
                        setEditingEvent(popoverEvent);
                        setPopoverEvent(null);
                        setPopoverAnchor(null);
                    }}
                />
            )}

            {editingEvent && (
                <EditMonthEventModal
                    event={editingEvent}
                    onClose={() => setEditingEvent(null)}
                    onSave={() => {
                        setEditingEvent(null);
                        loadMonthEvents();
                        onReload();
                    }}
                    onShowToast={onShowToast}
                />
            )}
        </>
    );
};

export default MonthCalendar;